import React, { useState } from 'react';
import { Button, InputPassword, sparkToast, useReadOnly } from '@spark';

import SettingsSectionCard from './SettingsSectionCard.jsx';
import EditableSecretInput from './EditableSecretInput.jsx';
import { useAuth } from '@/features/auth/AuthContext';
import './settings.css';

function validateHuggingFaceToken(token) {
    const trimmed = (token || '').trim();
    if (!trimmed) return 'Hugging Face token cannot be empty';
    if (!trimmed.startsWith('hf_')) return 'Hugging Face token should start with "hf_"';
    if (trimmed.length < 20) return 'Token appears to be too short';
    return null;
}

// The browser never receives the token; the backend reports only whether one
// is stored and its last four characters, which is all the row displays.
export function getMaskedHuggingFaceToken(last4) {
    return `hf_...${last4 || '••••'}`;
}

/**
 * Hugging Face Token tab: save / replace / delete the caller's token,
 * restyled on the shared Spark section patterns (description with docs
 * link, 360px InputPassword with lock prefix, masked-token row).
 *
 * Props carry masked metadata only (`configured`, `last4`); the stored token
 * is never present client-side, so "Edit" is replace-only and there is no
 * copy action.
 */
const HuggingFaceToken = ({ configured = false, last4 = null, onTokenUpdate }) => {
    const { role, isAuthEnabled } = useAuth();
    const canEdit = !isAuthEnabled || role === 'Admin' || role === 'Editor';
    const { readOnly } = useReadOnly();
    const [newToken, setNewToken] = useState('');

    const handleAddToken = () => {
        const error = validateHuggingFaceToken(newToken);
        if (error) {
            sparkToast.error(error);
            return;
        }
        onTokenUpdate(newToken.trim(), 'saved');
        setNewToken('');
    };

    return (
        <SettingsSectionCard iconSlug="huggingface" title="Hugging Face Token">
            <div className="settings-providers__panel">
                <p className="settings-text settings-providers__description fs-unmask">
                    Add your Hugging Face token to access private datasets and
                    models. Need a Hugging Face token? Get one
                    {' '}
                    <a
                        className="settings-link"
                        href="https://huggingface.co/settings/tokens"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        here
                    </a>
                    .
                </p>

                {configured ? (
                    <EditableSecretInput
                        replaceOnly
                        copyEnabled={false}
                        maskedDisplay={getMaskedHuggingFaceToken(last4)}
                        placeholder="Enter a new Hugging Face token"
                        editable={canEdit}
                        confirmDeleteTitle="Delete this Hugging Face token?"
                        confirmDeleteDescription="Private Hugging Face datasets and models will no longer be accessible."
                        onSave={async (edited) => {
                            const error = validateHuggingFaceToken(edited);
                            if (error) {
                                sparkToast.error(error);
                                // Reject so the editor stays open for a fix.
                                throw new Error(error);
                            }
                            onTokenUpdate(edited.trim(), 'edited');
                        }}
                        onDelete={canEdit ? () => onTokenUpdate(null, 'deleted') : undefined}
                    />
                ) : (
                    <div className="settings-keys__row">
                        <InputPassword
                            size="large"
                            showLabel={false}
                            showPrefixIcon
                            value={newToken}
                            onChange={(e) => setNewToken(e.target.value)}
                            placeholder="Enter your Hugging Face token"
                            disabled={readOnly || !canEdit}
                            className="settings-secret__input"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleAddToken();
                            }}
                        />
                        {newToken.trim() ? (
                            <Button size="tiny" variant="ghost" disableWhenReadOnly onClick={handleAddToken}>
                                Save
                            </Button>
                        ) : null}
                    </div>
                )}
            </div>
        </SettingsSectionCard>
    );
};

export default HuggingFaceToken;
